import React from "react";
import { Helmet } from "react-helmet-async";
import Grid, { type Actions, type Columns } from "../../component/Grid";
import { useSearchParams } from "react-router-dom";
import useLocale, { I18N } from "../../util/i18n";
import { useCrumbs } from "../../component/breadcrumbs";
import { getLogs, getUser, type LogData, deleteLogs, isOperationGranted } from "../../api/admin";
import { OptionType } from "../../component/select";
import InputBox from "../../component/input-box";
import { type ListResponse } from "../../api/types";
import { CancelablePromise } from "../../util/client";
import Toast from "../../component/toast";
import Sku from "../../component/sku";
import { format } from "date-fns";
import { MDBBadge, MDBBtn, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader } from "mdb-react-ui-kit";
import env from "../../env.json";
import Lightbox, { type LightBoxRef } from "../../component/lightbox";

export default function Logs() {
    let { 0: query, 1: setQuery } = useSearchParams();
    let { 0: users, 1: setUsers } = React.useState<Array<OptionType>>([]);
    let { 0: stat, 1: setStat } = React.useState<React.ReactNode>('');
    let { 0: type, 1: setType } = React.useState(0);
    let { 0: setCrumbs } = useCrumbs();
    let { 0: __ } = useLocale();
    let columns = React.useMemo<Columns>(() => ({
        user_id: {
            label: __('Username'),
            type: 'select',
            options: users,
            filterable: 'filter[user_id]',
            format: (value: number | string) => users.find(i => i.value === value.toString())?.label || value.toString()
        },
        sku: {
            label: type % 2 ? __('RFID Label') : 'SKU',
            filterable: 'filter[sku][like]',
            format: (value, item) => (
                <>
                    <Sku full>{value}</Sku>
                    {item.remark?.qty ? (
                        <MDBBadge color={item.remark.qty > 0 ? 'info' : 'danger'} className="ms-2">{item.remark.qty}</MDBBadge>
                    ) : null}
                </>
            )
        },
        order_id: {
            label: __(type % 2 ? 'Order' : 'Location'),
            filterable: type % 2 ? 'filter[order_id][like]' : undefined,
            format: (value, item) => item.remark?.order_id_reason ? (<>{value}<br />{__(item.remark.order_id_reason)}</>) : value
        },
        point: {
            label: __('Points'),
            className: 'w-1 text-end',
            format: (value: number) => parseFloat(value.toString()).toFixed(2)
        },
        ...(type % 2 ? {
            duration: {
                label: __('Duration'),
                className: 'w-1 text-center text-nowrap',
                format: (value: number) => value ? (
                    (value > 3600 ? Math.floor(value / 3600).toString().padStart(2, '0') + ':' : '') +
                    Math.floor(value / 60 % 60).toString().padStart(2, '0') + ':' +
                    Math.floor(value % 60).toString().padStart(2, '0')
                ) : 'NaN'
            },
            status: {
                label: __('Status'),
                className: 'w-1 text-center text-nowrap',
                format: (value, item) => {
                    let status;
                    switch (parseInt(value)) {
                        case 0: status = __('Deleted'); break;
                        case 4:
                        case -1: status = __('Replaced New'); break;
                        case 2: status = __('Found Parts from open Box'); break;
                        case 3: status = __('Manually Fixed'); break;
                        default: status = __('Normal');
                    }
                    return (
                        <>
                            <span>{status}</span>
                            {value != 0 && value != 1 ? (
                                <>
                                    <br />
                                    <a href="#" onClick={showClaim.bind(null, item as LogData)}>{__('Claim Information')}</a>
                                </>
                            ) : null}
                        </>
                    );
                }
            }
        } : {}),
        created_at: {
            label: __('Date'),
            className: 'text-center',
            format: (value: number) => format(value * 1000, 'yyyy-MM-dd HH:mm')
        }
    }), [__, type, users]);
    let claim = React.useRef<(item: LogData) => void>(null);
    let showClaim = React.useCallback((item: LogData, e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        claim.current?.(item);
    }, []);
    let actions = React.useMemo<Actions>(() => ([
        {
            label: __('Delete'),
            onClick: (item: LogData) => {
                if (window.confirm(__('Are you sure to delete this? The deleted data cannot be recovered.'))) {
                    deleteLogs(item.id).then(() => {
                        Toast.show(__('Data has been deleted.')).then(() => window.location.reload())
                    })
                }
            }
        }
    ]), [__]);
    let filter = React.useCallback((search: URLSearchParams) => (
        <>
            <InputBox label={__('Start at')}>
                <input type="datetime-local"
                    className="form-control active" name="filter[created_at][gte]"
                    defaultValue={search.get('filter[created_at][gte]') ? format(search.get('filter[created_at][gte]')!, 'yyyy-MM-ddTHH:mm') : ''}
                />
            </InputBox>
            <InputBox label={__('End at')}>
                <input type="datetime-local"
                    className="form-control active" name="filter[created_at][lte]"
                    defaultValue={search.get('filter[created_at][lte]') ? format(search.get('filter[created_at][lte]')!, 'yyyy-MM-ddTHH:mm') : ''}
                />
            </InputBox>
        </>
    ), [__, setQuery]);
    let onReset = React.useCallback((search: URLSearchParams) => {
        return new URLSearchParams(search.has('filter[user_id]') ? [['filter[user_id]', search.get('filter[user_id]') as string], ['filter[type]', type.toString()]] : [['filter[type]', type.toString()]]);
    }, [type]);
    const doLoad = React.useCallback((params: Record<string, any>) => {
        if (params['filter[created_at][gte]']) {
            params['filter[created_at][gte]'] = ((new Date(params['filter[created_at][gte]'])).getTime() / 1000).toFixed(0);
        }
        if (params['filter[created_at][lte]']) {
            params['filter[created_at][lte]'] = ((new Date(params['filter[created_at][lte]'])).getTime() / 1000).toFixed(0);
        }
        for (let k in params) {
            if (params[k] === '') {
                delete params[k];
            } else if (k.endsWith('[like]')) {
                params[k] = '%' + params[k] + '%';
            }
        }
        return new CancelablePromise<ListResponse<LogData> | string>((resolve, reject) => getLogs(params).then(response => {
            if (typeof response === 'string') {
                resolve(response);
                return;
            }
            let total = 0, stat: React.ReactNode[] = [], data = [], replace = 0;
            response.total.sort((a, b) => b.status - a.status);
            for (let item of response.total) {
                total += item.count;
                switch (parseInt(item.status.toString())) {
                    case 0: stat.push(<React.Fragment key={item.status.toString()}><I18N>Deleted</I18N>: <span className="me-2">{item.count}</span></React.Fragment>); break;
                    case 4:
                    case -1: replace += item.count; break;
                    case 2: stat.push(<React.Fragment key={item.status.toString()}><I18N>Found Parts from open Box</I18N>: <span className="me-2">{item.count}</span></React.Fragment>); break;
                    case 3: stat.push(<React.Fragment key={item.status.toString()}><I18N>Manually Fixed</I18N>: <span className="me-2">{item.count}</span></React.Fragment>); break;
                    default: stat.push(<React.Fragment key={item.status.toString()}><I18N>Normal</I18N>: <span className="me-2">{item.count}</span></React.Fragment>);
                }
            }
            replace > 0 && stat.push(<React.Fragment key="-1"><I18N>Replaced New</I18N>: <span>{replace}</span></React.Fragment>);
            setStat(<span>{stat}</span>);
            for (let item of response.data) {
                if (typeof item.remark === 'string') {
                    try {
                        item.remark = JSON.parse(item.remark);
                    } catch { }
                }
                data.push(item);
            }
            resolve({ data, total });
        }, reject));
    }, [__]);
    let loadUsers = React.useCallback((type: number) => {
        let permissions = type === 1 ? 'claimKPI' : (type === 2 ? 'setStockStatus' : undefined);
        //@ts-ignore
        getUser({ filter: { status: { gte: 0 }, permissions }, limit: -1 }).then(response => {
            if (typeof response !== 'string') {
                let options = [];
                for (let item of response.data) {
                    options.push({ value: item.id.toString(), label: item.info?.nickname || item.username });
                }
                setUsers(options);
            }
        });
    }, [setUsers]);
    React.useEffect(() => {
        setCrumbs([{ label: 'User Management', link: '/user' }, 'Logs']);
    }, [setCrumbs]);
    React.useEffect(() => {
        if (query.has('filter[type]')) {
            let type = parseInt(query.get('filter[type]')!) || 0;
            setType(type);
            loadUsers(type);
        } else if (query.has('filter[user_id]')) {
            isOperationGranted({ operation: 'setStockStatus', user_id: query.get('filter[user_id]')! }).then(() => {
                setType(2);
                loadUsers(2);
            }, () => {
                setType(1);
                loadUsers(1);
            });
        } else {
            loadUsers(0);
        }
    }, [query, setType, loadUsers]);
    return (
        <>
            <Helmet>
                <title>{__(type === 1 ? 'Assemble Logs' : (type === 2 ? 'Organize Logs' : 'User Management'))}</title>
            </Helmet>
            <Grid columns={columns} actions={actions} query={query} setQuery={setQuery} doLoad={doLoad}
                additionalFilter={filter} afterPager={stat} onReset={onReset} />
            <Claim ref={claim} />
        </>
    );
}

const Claim = React.forwardRef<(log: LogData) => void>(function (_, ref) {
    let { 0: log, 1: setLog } = React.useState<LogData | null>(null);
    let { 0: __ } = useLocale();
    const lightbox = React.useRef<LightBoxRef>(null);
    let onClickImg = React.useCallback((index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (log?.remark?.images?.length) {
            lightbox.current?.show(log.remark.images, index);
        }
    }, [log]);
    React.useImperativeHandle(ref, () => setLog, [setLog]);
    return (
        <>
            <MDBModal open={log != null} onClose={setLog.bind(null, null)} tabIndex="-1">
                <MDBModalDialog centered size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>
                            <Sku full>{log?.sku || ''}</Sku>
                        </MDBModalHeader>
                        <MDBModalBody tag="table" className="table">
                            <colgroup>
                                <col width="1" />
                                <col />
                            </colgroup>
                            <tbody>
                                {log?.remark?.reason ? (
                                    <tr>
                                        <th className="text-nowrap">{__('Claim Reason')}</th>
                                        <td>{__(log.remark.reason)}</td>
                                    </tr>
                                ) : null}
                                {log?.remark?.part ? (
                                    <tr>
                                        <th className="text-nowrap">{__('Claim Parts')}</th>
                                        <td>{typeof log.remark.part === 'string' ? __(log.remark.part) : log.remark.part.map(__).join(', ')}</td>
                                    </tr>
                                ) : null}
                                {log?.remark?.comments ? (
                                    <tr>
                                        <th className="text-nowrap">{__('Comments')}</th>
                                        <td>{__(log.remark.comments)}</td>
                                    </tr>
                                ) : null}
                                {log?.remark?.images?.length ? (
                                    <tr>
                                        <th className="text-nowrap">{__('Images')}</th>
                                        <td>{log.remark.images.map((image, i) => (
                                            <img key={'img-' + i} src={image[0] === '/' ? env.RES_URL + image.substring(1) : image} width="50" height="50" alt="" onClick={onClickImg.bind(null, i)} />
                                        ))}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </MDBModalBody>
                        <MDBModalFooter className="justify-content-center">
                            <MDBBtn type="button" color="secondary" onClick={setLog.bind(null, null)}>{__('Close')}</MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
            <Lightbox ref={lightbox} />
        </>
    );
});
